/**
 * PulseFX Universal Pad Container
 * --------------------------------
 * A self-contained, framework-free pad picker + slider drawer + iframe host.
 *
 * Drop into any host (viz desktop, viz mobile, Flask shim) like so:
 *
 *   import { createPadContainer } from "./ui/padContainer";
 *
 *   const handle = createPadContainer({
 *     root: document.getElementById("pads")!,
 *     layout: "desktop",
 *     audioSource: () => audioEngine.getFeatures(),
 *     onPadChange: (pad) => console.log("pad:", pad.id),
 *     onParamChange: (padId, paramId, value) => store.set(padId, paramId, value),
 *   });
 *   // later: handle.setPad("sphere"); handle.setParam("speed", 1.5); handle.destroy();
 */

// ---------- Public API ---------------------------------------------------

export interface PadAudioFeatures {
  bass: number;
  mid: number;
  treble: number;
  level: number;
  beat: number;
  bpm: number;
  t: number;
  beatCount?: number;
  spectrum?: number[];
  waveform?: number[];
}

export type PadParamType = "float" | "color" | "select" | "toggle";
export type PadParamValue = number | string | boolean | number[];

export interface PadParamSchema {
  id: string;
  label: string;
  min?: number;
  max?: number;
  default: PadParamValue;
  step?: number;
  type?: PadParamType;
  options?: string[];
}

export interface PadManifestEntry {
  id: string;
  name: string;
  file: string;
  tags?: string[];
  tempo?: string;
  credit?: string;
  thumb?: string;
  params?: PadParamSchema[];
}

export interface PadSelectionItem {
  id: string;
  label: string;
  group?: string;
}

export interface PadContainerOptions {
  root: HTMLElement;
  manifestUrls?: string[];
  includePadIds?: string[];
  padBase?: string;
  initialPadId?: string;
  layout?: "desktop" | "mobile";
  audioSource?: () => PadAudioFeatures;
  onPadChange?: (pad: PadManifestEntry) => void;
  onParamChange?: (padId: string, paramId: string, value: PadParamValue) => void;
  onSelectionChange?: (padId: string, selectionId: string) => void;
}

export interface PadContainerHandle {
  setPad(id: string): void;
  setParam(paramId: string, value: PadParamValue): void;
  getState(): { pad: PadManifestEntry | null; values: Record<string, PadParamValue> };
  destroy(): void;
}

// ---------- Internal helpers --------------------------------------------

const STYLE_ID = "pfx-pad-container-style";
const PAD_MANIFEST_CACHE_BUST = "20260521-song-galaxy";
const STYLE_CSS = `
.pfx-pc { position: relative; width: 100%; height: 100%; display: flex; flex-direction: column;
  background: #0a0d18; color: #c9d8ee; font-family: "JetBrains Mono","SF Mono",Menlo,Consolas,monospace;
  font-size: 11px; box-sizing: border-box; overflow: hidden; }
.pfx-pc *, .pfx-pc *::before, .pfx-pc *::after { box-sizing: border-box; }
.pfx-pc-error { padding: 24px; color: #ff8090; }
.pfx-pc-error button { margin-top: 8px; }
.pfx-pc button, .pfx-pc input, .pfx-pc select { font-family: inherit; font-size: 11px; }
.pfx-pc button { background: #1a2340; color: #c9d8ee; border: 1px solid #2a3a60;
  padding: 6px 10px; border-radius: 4px; cursor: pointer; }
.pfx-pc button:hover { background: #243056; border-color: #80aaff; }
.pfx-pc-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.12em;
  color: #6b7a99; margin-bottom: 4px; }

/* Desktop layout */
.pfx-pc-desktop { flex-direction: row; }
.pfx-pc-picker { width: 280px; min-width: 280px; height: 100%; display: flex; flex-direction: column;
  border-right: 1px solid #1a2340; background: #07090f; }
.pfx-pc-search { padding: 12px; border-bottom: 1px solid #1a2340; display: flex; flex-direction: column; gap: 8px; }
.pfx-pc-search input { background: #0a0d18; color: #c9d8ee; border: 1px solid #2a3a60;
  padding: 6px 8px; border-radius: 4px; width: 100%; }
.pfx-pc-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.pfx-pc-tag { background: #1a2340; color: #80aaff; border: 1px solid transparent;
  padding: 2px 6px; border-radius: 999px; font-size: 9px; cursor: pointer; text-transform: lowercase; }
.pfx-pc-tag.active { background: #80aaff; color: #0a0d18; }
.pfx-pc-grid { flex: 1; overflow-y: auto; padding: 12px; display: grid;
  grid-template-columns: 1fr; gap: 8px; }
.pfx-pc-tile { width: 100%; height: 100px; border-radius: 6px; padding: 10px;
  cursor: pointer; border: 1px solid #1a2340; position: relative; display: flex;
  flex-direction: column; justify-content: space-between; transition: transform 0.1s, border-color 0.1s; }
.pfx-pc-tile:hover { transform: translateY(-1px); border-color: #2a3a60; }
.pfx-pc-tile.active { border-color: #80aaff; box-shadow: 0 0 0 2px rgba(128,170,255,0.3); }
.pfx-pc-tile-name { font-size: 12px; color: #fff; font-weight: 600;
  text-shadow: 0 1px 2px rgba(0,0,0,0.6); }
.pfx-pc-tile-tags { display: flex; flex-wrap: wrap; gap: 3px; }
.pfx-pc-tile-tag { background: rgba(0,0,0,0.4); color: #c9d8ee; padding: 1px 5px;
  border-radius: 999px; font-size: 9px; }

.pfx-pc-stage { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.pfx-pc-frame-wrap { flex: 1; position: relative; background: #000; min-height: 0; }
.pfx-pc-frame-wrap iframe { display: block; width: 100%; height: 100%; border: 0; }
.pfx-pc-loading, .pfx-pc-frame-error { position: absolute; inset: 0; display: flex;
  align-items: center; justify-content: center; flex-direction: column; gap: 12px;
  background: rgba(10,13,24,0.9); color: #c9d8ee; pointer-events: none; }
.pfx-pc-frame-error { pointer-events: auto; }

.pfx-pc-drawer { width: 320px; min-width: 320px; height: 100%; display: flex; flex-direction: column;
  border-left: 1px solid #1a2340; background: #07090f; }
.pfx-pc-drawer-head { padding: 14px; border-bottom: 1px solid #1a2340; }
.pfx-pc-drawer-name { color: #fff; font-size: 13px; font-weight: 600; }
.pfx-pc-drawer-credit { color: #6b7a99; font-size: 10px; margin-top: 4px; }
.pfx-pc-variant-row { display: none; align-items: center; gap: 6px; padding: 8px 14px;
  border-bottom: 1px solid #1a2340; background: #0a0d18; }
.pfx-pc-variant-row.is-on { display: flex; }
.pfx-pc-variant-row .pfx-pc-variant-label { font-size: 9px; text-transform: uppercase;
  letter-spacing: 0.12em; color: #6b7a99; font-weight: 700; }
.pfx-pc-variant { flex: 1; background: #07090f; color: #c9d8ee;
  border: 1px solid #2a3a60; padding: 4px 6px; border-radius: 4px; font-family: inherit; font-size: 11px; }
.pfx-pc-variant:focus { border-color: #80aaff; outline: none; }
.pfx-pc-controls { flex: 1; overflow-y: auto; padding: 12px; display: flex; flex-direction: column; gap: 14px; }
.pfx-pc-empty { color: #6b7a99; font-style: italic; padding: 24px 12px; text-align: center; font-size: 11px; }
.pfx-pc-control { display: flex; flex-direction: column; gap: 4px; }
.pfx-pc-control-row { display: flex; align-items: center; gap: 8px; }
.pfx-pc-control input[type="range"] { flex: 1; accent-color: #80aaff; }
.pfx-pc-badge { background: #1a2340; color: #80aaff; padding: 2px 6px; border-radius: 4px;
  font-size: 10px; min-width: 42px; text-align: center; font-variant-numeric: tabular-nums; }
.pfx-pc-control input[type="color"] { width: 40px; height: 24px; border: 1px solid #2a3a60;
  background: transparent; padding: 0; border-radius: 4px; cursor: pointer; }
.pfx-pc-control select { flex: 1; background: #0a0d18; color: #c9d8ee; border: 1px solid #2a3a60;
  padding: 4px 6px; border-radius: 4px; }
.pfx-pc-switch { position: relative; width: 36px; height: 20px; }
.pfx-pc-switch input { opacity: 0; width: 0; height: 0; }
.pfx-pc-switch-slider { position: absolute; inset: 0; background: #1a2340;
  border: 1px solid #2a3a60; border-radius: 999px; cursor: pointer; transition: 0.15s; }
.pfx-pc-switch-slider::before { content: ""; position: absolute; left: 2px; top: 2px;
  width: 14px; height: 14px; background: #c9d8ee; border-radius: 50%; transition: 0.15s; }
.pfx-pc-switch input:checked + .pfx-pc-switch-slider { background: #80aaff; border-color: #80aaff; }
.pfx-pc-switch input:checked + .pfx-pc-switch-slider::before { transform: translateX(16px); background: #0a0d18; }
.pfx-pc-drawer-foot { padding: 12px; border-top: 1px solid #1a2340; }
.pfx-pc-drawer-foot button { width: 100%; }
.pfx-pc-special { border: 1px solid #243456; border-radius: 6px; background: #0d1224; padding: 10px; }
.pfx-pc-special-title { color: #fff; font-size: 12px; font-weight: 700; margin-bottom: 4px; }
.pfx-pc-special-desc { color: #7f8daa; font-size: 10px; line-height: 1.45; margin-bottom: 8px; }
.pfx-pc-special-row { display: flex; gap: 6px; align-items: center; margin-bottom: 8px; }
.pfx-pc-special-row input { flex: 1; min-width: 0; background: #07090f; color: #c9d8ee;
  border: 1px solid #2a3a60; padding: 6px 8px; border-radius: 4px; }
.pfx-pc-special-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 8px; }
.pfx-pc-special textarea { width: 100%; min-height: 86px; resize: vertical; background: #07090f;
  color: #c9d8ee; border: 1px solid #2a3a60; border-radius: 4px; padding: 7px 8px;
  font: 10px/1.45 "JetBrains Mono","SF Mono",Menlo,Consolas,monospace; }
.pfx-pc-special-label { display: block; color: #6b7a99; text-transform: uppercase;
  letter-spacing: 0.12em; font-size: 9px; margin: 8px 0 4px; }
.pfx-pc-special-status { min-height: 14px; color: #80aaff; font-size: 10px; line-height: 1.4; margin-top: 6px; }
.pfx-pc-special-status.is-error { color: #ff8a8a; }
.pfx-pc-special-status.is-ok { color: #7afcaa; }

/* Mobile layout */
.pfx-pc-mobile { flex-direction: column; }
.pfx-pc-topbar { display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; border-bottom: 1px solid #1a2340; background: #07090f; gap: 8px; }
.pfx-pc-topbar-name { color: #fff; font-size: 12px; font-weight: 600; flex: 1;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.pfx-pc-overlay { position: absolute; inset: 0; z-index: 20; background: rgba(7,9,15,0.97);
  display: none; flex-direction: column; }
.pfx-pc-overlay.open { display: flex; }
.pfx-pc-sheet { position: absolute; left: 0; right: 0; bottom: 0; z-index: 10;
  background: #07090f; border-top: 1px solid #1a2340; max-height: 70%;
  display: flex; flex-direction: column; transition: transform 0.2s ease;
  border-radius: 12px 12px 0 0; box-shadow: 0 -8px 24px rgba(0,0,0,0.4); }
.pfx-pc-sheet-handle { height: 28px; display: flex; align-items: center; justify-content: center;
  cursor: grab; }
.pfx-pc-sheet-handle::before { content: ""; width: 40px; height: 4px; background: #2a3a60;
  border-radius: 2px; }
.pfx-pc-sheet.collapsed { transform: translateY(calc(100% - 160px)); }
.pfx-pc-sheet .pfx-pc-controls { max-height: 60vh; }
`;

function ensureStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = STYLE_CSS;
  document.head.appendChild(el);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string>,
  children?: (Node | string)[],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (k === "class") node.className = v;
      else node.setAttribute(k, v);
    }
  }
  if (children) {
    for (const c of children) {
      node.append(typeof c === "string" ? document.createTextNode(c) : c);
    }
  }
  return node;
}

function resolvePadUrl(file: string, base?: string): string {
  try {
    if (/^https?:\/\//i.test(file) || file.startsWith("/")) return file;
    if (base) return new URL(file, base).toString();
    return new URL(file, window.location.href).toString();
  } catch {
    return file;
  }
}

function deriveStep(min: number, max: number): number {
  const range = Math.abs(max - min);
  if (range <= 1) return 0.01;
  if (range <= 10) return 0.1;
  return 1;
}

function fmtNumber(n: number): string {
  if (!isFinite(n)) return "—";
  if (Math.abs(n) >= 100) return n.toFixed(0);
  if (Math.abs(n) >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

// ---------- Implementation -----------------------------------------------

export function createPadContainer(opts: PadContainerOptions): PadContainerHandle {
  ensureStyles();

  const layout = opts.layout ?? "desktop";
  const manifestUrls = opts.manifestUrls ?? ["/api/pads/manifest", `/pads/manifest.json?v=${PAD_MANIFEST_CACHE_BUST}`];

  // Per-pad persisted user values: Record<padId, Record<paramId, value>>
  const persisted: Record<string, Record<string, PadParamValue>> = {};
  // Per-pad persisted selection (variant) id.
  const persistedSelection: Record<string, string> = (() => {
    try {
      const raw = typeof localStorage !== "undefined"
        ? localStorage.getItem("pfx-pc-pad-selection-v1") : null;
      if (!raw) return {};
      const p = JSON.parse(raw);
      return (p && typeof p === "object") ? p as Record<string, string> : {};
    } catch { return {}; }
  })();
  function persistSelection(padId: string, id: string): void {
    persistedSelection[padId] = id;
    try {
      localStorage.setItem("pfx-pc-pad-selection-v1", JSON.stringify(persistedSelection));
    } catch { /* ignore */ }
  }
  // libId → backend share id (set by pads via pfx:padShareId).
  const libShareIds: Record<string, string> = {};

  let pads: PadManifestEntry[] = [];
  let requestedPadId: string | null = opts.initialPadId ?? null;
  let currentPad: PadManifestEntry | null = null;
  let currentSchema: PadParamSchema[] = [];
  let currentSelection: { items: PadSelectionItem[]; currentId: string | null } | null = null;
  let activeTags = new Set<string>();
  let searchTerm = "";
  let destroyed = false;
  let rafHandle: number | null = null;
  let readyTimeout: number | null = null;
  let messageHandler: ((ev: MessageEvent) => void) | null = null;

  // Refs to controls keyed by paramId so we can reflect external updates without loops.
  let controlRefs: Record<string, (v: PadParamValue) => void> = {};

  const root = opts.root;
  root.classList.add("pfx-pc", layout === "mobile" ? "pfx-pc-mobile" : "pfx-pc-desktop");
  root.innerHTML = "";

  // Containers we'll fill once manifest loads.
  const stageEl = el("div", { class: "pfx-pc-stage" });
  const frameWrap = el("div", { class: "pfx-pc-frame-wrap" });
  let iframe: HTMLIFrameElement | null = null;
  let loadingOverlay: HTMLElement | null = null;
  let errorOverlay: HTMLElement | null = null;
  stageEl.appendChild(frameWrap);

  // Drawer (controls)
  const drawerEl = el("div", { class: layout === "mobile" ? "pfx-pc-sheet collapsed" : "pfx-pc-drawer" });
  let drawerHead: HTMLElement;
  let controlsEl: HTMLElement;
  let drawerFoot: HTMLElement;
  if (layout === "mobile") {
    const handle = el("div", { class: "pfx-pc-sheet-handle" });
    handle.addEventListener("click", () => drawerEl.classList.toggle("collapsed"));
    drawerEl.appendChild(handle);
  }
  drawerHead = el("div", { class: "pfx-pc-drawer-head" });
  const variantRow = el("div", { class: "pfx-pc-variant-row" });
  const variantLabel = el("span", { class: "pfx-pc-variant-label" }, ["Variant"]);
  const variantSelect = el("select", { class: "pfx-pc-variant", "aria-label": "Pad variant" }) as HTMLSelectElement;
  variantSelect.addEventListener("change", () => {
    const v = variantSelect.value;
    if (!v || !currentSelection) return;
    currentSelection.currentId = v;
    if (currentPad) {
      persistSelection(currentPad.id, v);
      opts.onSelectionChange?.(currentPad.id, v);
    }
    postToIframe({ type: "pfx:command", command: "selection", value: { id: v } });
  });
  variantRow.append(variantLabel, variantSelect);
  controlsEl = el("div", { class: "pfx-pc-controls" });
  drawerFoot = el("div", { class: "pfx-pc-drawer-foot" });
  drawerEl.append(drawerHead, variantRow, controlsEl, drawerFoot);

  // Picker
  const pickerEl = el("div", { class: "pfx-pc-picker" });
  const searchBox = el("div", { class: "pfx-pc-search" });
  const searchInput = el("input", { type: "text", placeholder: "Search pads…" });
  const tagsEl = el("div", { class: "pfx-pc-tags" });
  searchBox.append(
    el("div", { class: "pfx-pc-label" }, ["Browse"]),
    searchInput,
    tagsEl,
  );
  const gridEl = el("div", { class: "pfx-pc-grid" });
  pickerEl.append(searchBox, gridEl);

  // Mobile-specific: top bar + overlay grid
  let mobileOverlay: HTMLElement | null = null;
  let mobileTopName: HTMLElement | null = null;
  if (layout === "mobile") {
    const topbar = el("div", { class: "pfx-pc-topbar" });
    mobileTopName = el("div", { class: "pfx-pc-topbar-name" }, ["Loading…"]);
    const browseBtn = el("button", {}, ["Browse"]);
    browseBtn.addEventListener("click", () => mobileOverlay?.classList.add("open"));
    topbar.append(mobileTopName, browseBtn);
    root.appendChild(topbar);

    mobileOverlay = el("div", { class: "pfx-pc-overlay" });
    mobileOverlay.appendChild(pickerEl);
    // close on tile tap is handled in renderTiles
  }

  if (layout === "desktop") {
    root.append(pickerEl, stageEl, drawerEl);
  } else {
    root.append(stageEl, drawerEl, mobileOverlay!);
  }

  searchInput.addEventListener("input", () => {
    searchTerm = searchInput.value.trim().toLowerCase();
    renderTiles();
  });

  // ---------- Rendering ----------

  function renderTags(): void {
    tagsEl.innerHTML = "";
    const all = new Set<string>();
    for (const p of pads) (p.tags ?? []).forEach((t) => all.add(t));
    const sorted = Array.from(all).sort();
    for (const tag of sorted) {
      const btn = el("span", {
        class: "pfx-pc-tag" + (activeTags.has(tag) ? " active" : ""),
      }, [tag]);
      btn.addEventListener("click", () => {
        if (activeTags.has(tag)) activeTags.delete(tag);
        else activeTags.add(tag);
        renderTags();
        renderTiles();
      });
      tagsEl.appendChild(btn);
    }
  }

  function tileMatches(p: PadManifestEntry): boolean {
    if (searchTerm) {
      const hay = (p.name + " " + (p.tags ?? []).join(" ")).toLowerCase();
      if (!hay.includes(searchTerm)) return false;
    }
    if (activeTags.size > 0) {
      const padTags = new Set(p.tags ?? []);
      for (const t of activeTags) if (!padTags.has(t)) return false;
    }
    return true;
  }

  function renderTiles(): void {
    gridEl.innerHTML = "";
    const filtered = pads.filter(tileMatches);
    if (filtered.length === 0) {
      gridEl.appendChild(el("div", { class: "pfx-pc-empty" }, ["No pads match."]));
      return;
    }
    for (const pad of filtered) {
      const isActive = currentPad?.id === pad.id;
      const bg = pad.thumb && /^#/.test(pad.thumb)
        ? `linear-gradient(135deg, ${pad.thumb}, ${shade(pad.thumb, -20)})`
        : "linear-gradient(135deg,#1a2340,#0a0d18)";
      const tile = el("div", {
        class: "pfx-pc-tile" + (isActive ? " active" : ""),
        style: `background: ${bg};`,
        title: pad.credit ?? pad.name,
      });
      tile.append(el("div", { class: "pfx-pc-tile-name" }, [pad.name]));
      const tagWrap = el("div", { class: "pfx-pc-tile-tags" });
      for (const t of (pad.tags ?? []).slice(0, 3)) {
        tagWrap.appendChild(el("span", { class: "pfx-pc-tile-tag" }, [t]));
      }
      tile.appendChild(tagWrap);
      tile.addEventListener("click", () => {
        loadPad(pad.id);
        if (layout === "mobile") mobileOverlay?.classList.remove("open");
      });
      gridEl.appendChild(tile);
    }
  }

  function shade(hex: string, percent: number): string {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex);
    if (!m) return hex;
    const num = parseInt(m[1], 16);
    let r = (num >> 16) & 0xff;
    let g = (num >> 8) & 0xff;
    let b = num & 0xff;
    const f = (c: number) => Math.max(0, Math.min(255, Math.round(c + (255 * percent) / 100)));
    r = f(r); g = f(g); b = f(b);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
  }

  function renderVariantSelect(): void {
    variantSelect.innerHTML = "";
    if (!currentSelection || currentSelection.items.length === 0) {
      variantRow.classList.remove("is-on");
      return;
    }
    const ungrouped: PadSelectionItem[] = [];
    const groupOrder: string[] = [];
    const groups: Record<string, PadSelectionItem[]> = {};
    for (const it of currentSelection.items) {
      if (it.group) {
        if (!groups[it.group]) { groups[it.group] = []; groupOrder.push(it.group); }
        groups[it.group].push(it);
      } else {
        ungrouped.push(it);
      }
    }
    const addOpt = (parent: HTMLElement | HTMLOptGroupElement, it: PadSelectionItem): void => {
      const o = document.createElement("option");
      o.value = it.id;
      o.textContent = it.label || it.id;
      parent.appendChild(o);
    };
    for (const it of ungrouped) addOpt(variantSelect, it);
    for (const g of groupOrder) {
      const grp = document.createElement("optgroup");
      grp.label = g;
      for (const it of groups[g]) addOpt(grp, it);
      variantSelect.appendChild(grp);
    }
    if (currentSelection.currentId) variantSelect.value = currentSelection.currentId;
    variantRow.classList.add("is-on");
  }

  function setSpecialStatus(text: string, kind: "ok" | "error" | "info" = "info"): void {
    const status = controlsEl.querySelector<HTMLElement>(".pfx-pc-special-status");
    if (!status) return;
    status.textContent = text;
    status.classList.toggle("is-ok", kind === "ok");
    status.classList.toggle("is-error", kind === "error");
  }

  function buildTextArea(placeholder: string, rows: number): HTMLTextAreaElement {
    const area = document.createElement("textarea");
    area.rows = rows;
    area.spellcheck = false;
    area.placeholder = placeholder;
    return area;
  }

  function addSpecialLabel(host: HTMLElement, label: string): void {
    host.appendChild(el("span", { class: "pfx-pc-special-label" }, [label]));
  }

  function buildShaderToyControls(): HTMLElement {
    const box = el("div", { class: "pfx-pc-special" });
    box.append(
      el("div", { class: "pfx-pc-special-title" }, ["ShaderToy / Custom GLSL"]),
      el("div", { class: "pfx-pc-special-desc" }, [
        "Paste Shadertoy-style fragment code, or import a Shadertoy URL through the configured proxy. Audio is available as iChannel0 plus iAudio and iBeat.",
      ]),
    );

    const urlRow = el("div", { class: "pfx-pc-special-row" });
    const urlInput = el("input", { type: "url", placeholder: "https://www.shadertoy.com/view/..." }) as HTMLInputElement;
    const importBtn = el("button", {}, ["Import"]);
    importBtn.addEventListener("click", () => {
      const url = urlInput.value.trim();
      if (!url) { setSpecialStatus("Enter a Shadertoy URL or shader ID.", "error"); return; }
      postToIframe({ type: "pfx:command", command: "importShaderUrl", value: { url } });
      setSpecialStatus("Importing Shadertoy shader...");
    });
    urlRow.append(urlInput, importBtn);
    box.appendChild(urlRow);

    addSpecialLabel(box, "Fragment GLSL");
    const src = buildTextArea(
      "Paste mainImage(out vec4 fragColor, in vec2 fragCoord) or full Shadertoy image-pass GLSL...",
      7,
    );
    box.appendChild(src);

    const actions = el("div", { class: "pfx-pc-special-row" });
    const applyBtn = el("button", {}, ["Apply shader"]);
    const clearBtn = el("button", {}, ["Clear"]);
    const deleteBtn = el("button", {}, ["Delete custom"]);
    applyBtn.addEventListener("click", () => {
      const code = src.value.trim();
      if (!code) { setSpecialStatus("Paste GLSL first.", "error"); return; }
      postToIframe({ type: "pfx:command", command: "applyGlsl", value: { src: code } });
      setSpecialStatus("Applying shader...");
    });
    clearBtn.addEventListener("click", () => { src.value = ""; setSpecialStatus(""); });
    deleteBtn.addEventListener("click", () => postToIframe({ type: "pfx:command", command: "deleteShader" }));
    actions.append(applyBtn, clearBtn, deleteBtn);
    box.append(actions, el("div", { class: "pfx-pc-special-status" }));
    return box;
  }

  function buildCodePenControls(): HTMLElement {
    const box = el("div", { class: "pfx-pc-special" });
    box.append(
      el("div", { class: "pfx-pc-special-title" }, ["CodePen.io Code Entry"]),
      el("div", { class: "pfx-pc-special-desc" }, [
        "Paste CodePen HTML, CSS, and JS. The runner injects PulseFX beat data as window.PulseFXAudio, pulsefx-audio events, and CSS variables like --pfx-beat.",
      ]),
    );

    const urlRow = el("div", { class: "pfx-pc-special-row" });
    const urlInput = el("input", { type: "url", placeholder: "https://codepen.io/user/pen/..." }) as HTMLInputElement;
    const importBtn = el("button", {}, ["Import URL"]);
    importBtn.addEventListener("click", () => {
      const url = urlInput.value.trim();
      if (!url) { setSpecialStatus("Enter a CodePen URL.", "error"); return; }
      postToIframe({ type: "pfx:command", command: "importCodePenUrl", value: { url } });
      setSpecialStatus("Trying CodePen source import...");
    });
    urlRow.append(urlInput, importBtn);
    box.appendChild(urlRow);

    const nameInput = el("input", { type: "text", placeholder: "Name this visualizer" }) as HTMLInputElement;
    box.appendChild(nameInput);

    addSpecialLabel(box, "HTML");
    const html = buildTextArea("<canvas id=\"c\"></canvas>", 5);
    box.appendChild(html);
    addSpecialLabel(box, "CSS");
    const css = buildTextArea("body { margin: 0; overflow: hidden; background: #000; }", 4);
    box.appendChild(css);
    addSpecialLabel(box, "JS");
    const js = buildTextArea("window.addEventListener('pulsefx-audio', (event) => { const f = event.detail; });", 7);
    box.appendChild(js);

    const actions = el("div", { class: "pfx-pc-special-row" });
    const applyBtn = el("button", {}, ["Apply code"]);
    const starterBtn = el("button", {}, ["Beat starter"]);
    const clearBtn = el("button", {}, ["Clear"]);
    applyBtn.addEventListener("click", () => {
      postToIframe({
        type: "pfx:command",
        command: "applyCodePen",
        value: { name: nameInput.value.trim(), html: html.value, css: css.value, js: js.value },
      });
      setSpecialStatus("Applying CodePen visualizer...");
    });
    starterBtn.addEventListener("click", () => {
      postToIframe({ type: "pfx:command", command: "loadCodePenStarter" });
      setSpecialStatus("Loaded beat-reactive starter.", "ok");
    });
    clearBtn.addEventListener("click", () => {
      nameInput.value = "";
      html.value = "";
      css.value = "";
      js.value = "";
      setSpecialStatus("");
    });
    actions.append(applyBtn, starterBtn, clearBtn);
    box.append(actions, el("div", { class: "pfx-pc-special-status" }));
    return box;
  }

  function buildSpecialControls(): HTMLElement | null {
    if (!currentPad) return null;
    if (currentPad.id === "shadertoy") return buildShaderToyControls();
    if (currentPad.id === "codepen") return buildCodePenControls();
    return null;
  }

  function renderDrawer(): void {
    drawerHead.innerHTML = "";
    controlsEl.innerHTML = "";
    drawerFoot.innerHTML = "";
    controlRefs = {};

    if (!currentPad) {
      drawerHead.appendChild(el("div", { class: "pfx-pc-drawer-name" }, ["No pad"]));
      return;
    }
    drawerHead.append(
      el("div", { class: "pfx-pc-drawer-name" }, [currentPad.name]),
      el("div", { class: "pfx-pc-drawer-credit" }, [currentPad.credit ?? ""]),
    );

    const specialControls = buildSpecialControls();
    if (specialControls) controlsEl.appendChild(specialControls);

    if (currentSchema.length === 0) {
      if (!specialControls) controlsEl.appendChild(el("div", { class: "pfx-pc-empty" }, ["No controls — preset only"]));
      return;
    }

    const stored = persisted[currentPad.id] ?? {};
    for (const param of currentSchema) {
      const initial = stored[param.id] !== undefined ? stored[param.id] : param.default;
      const ctl = buildControl(param, initial);
      controlsEl.appendChild(ctl);
    }

    const resetBtn = el("button", {}, ["Reset to defaults"]);
    resetBtn.addEventListener("click", () => {
      if (!currentPad) return;
      delete persisted[currentPad.id];
      for (const p of currentSchema) {
        controlRefs[p.id]?.(p.default);
        sendParam(p.id, p.default, /*emit*/ true);
      }
    });
    drawerFoot.appendChild(resetBtn);
  }

  function buildControl(param: PadParamSchema, initial: PadParamValue): HTMLElement {
    const wrap = el("div", { class: "pfx-pc-control" });
    wrap.appendChild(el("div", { class: "pfx-pc-label" }, [param.label]));
    const row = el("div", { class: "pfx-pc-control-row" });
    wrap.appendChild(row);

    const type: PadParamType = param.type ?? "float";

    if (type === "color") {
      const input = el("input", { type: "color" }) as HTMLInputElement;
      input.value = typeof initial === "string" ? initial : "#80aaff";
      input.addEventListener("input", () => sendParam(param.id, input.value, true));
      row.appendChild(input);
      controlRefs[param.id] = (v) => { if (typeof v === "string") input.value = v; };
    } else if (type === "select") {
      const sel = el("select") as HTMLSelectElement;
      for (const opt of param.options ?? []) {
        const o = el("option", { value: opt }, [opt]) as HTMLOptionElement;
        sel.appendChild(o);
      }
      sel.value = String(initial);
      sel.addEventListener("change", () => sendParam(param.id, sel.value, true));
      row.appendChild(sel);
      controlRefs[param.id] = (v) => { sel.value = String(v); };
    } else if (type === "toggle") {
      const lab = el("label", { class: "pfx-pc-switch" });
      const cb = el("input", { type: "checkbox" }) as HTMLInputElement;
      cb.checked = Boolean(initial);
      const slider = el("span", { class: "pfx-pc-switch-slider" });
      lab.append(cb, slider);
      cb.addEventListener("change", () => sendParam(param.id, cb.checked, true));
      row.appendChild(lab);
      controlRefs[param.id] = (v) => { cb.checked = Boolean(v); };
    } else {
      // float
      const min = param.min ?? 0;
      const max = param.max ?? 1;
      const step = param.step ?? deriveStep(min, max);
      const input = el("input", {
        type: "range",
        min: String(min),
        max: String(max),
        step: String(step),
      }) as HTMLInputElement;
      const num = typeof initial === "number" ? initial : Number(initial) || 0;
      input.value = String(num);
      const badge = el("span", { class: "pfx-pc-badge" }, [fmtNumber(num)]);
      input.addEventListener("input", () => {
        const v = parseFloat(input.value);
        badge.textContent = fmtNumber(v);
        sendParam(param.id, v, true);
      });
      row.append(input, badge);
      controlRefs[param.id] = (v) => {
        const nv = typeof v === "number" ? v : Number(v) || 0;
        input.value = String(nv);
        badge.textContent = fmtNumber(nv);
      };
    }
    return wrap;
  }

  // ---------- Iframe + audio bridge ----------

  function postToIframe(msg: unknown): void {
    if (!iframe || !iframe.contentWindow) return;
    try { iframe.contentWindow.postMessage(msg, "*"); } catch { /* ignore */ }
  }

  function sendParam(paramId: string, value: PadParamValue, emit: boolean): void {
    if (!currentPad) return;
    if (!persisted[currentPad.id]) persisted[currentPad.id] = {};
    persisted[currentPad.id][paramId] = value;
    postToIframe({ type: "pfx:command", command: "param", value: { id: paramId, value } });
    if (emit) opts.onParamChange?.(currentPad.id, paramId, value);
  }

  function startAudioPump(): void {
    stopAudioPump();
    if (!opts.audioSource) return;
    const tick = () => {
      if (destroyed) return;
      if (opts.audioSource && iframe?.contentWindow) {
        try {
          const features = opts.audioSource();
          postToIframe({ type: "pfx:audio", features });
        } catch { /* ignore */ }
      }
      rafHandle = requestAnimationFrame(tick);
    };
    rafHandle = requestAnimationFrame(tick);
  }
  function stopAudioPump(): void {
    if (rafHandle != null) cancelAnimationFrame(rafHandle);
    rafHandle = null;
  }

  function clearReadyTimeout(): void {
    if (readyTimeout != null) { clearTimeout(readyTimeout); readyTimeout = null; }
  }

  function showOverlay(text: string, retry?: () => void): void {
    if (loadingOverlay) loadingOverlay.remove();
    if (errorOverlay) errorOverlay.remove();
    const ov = el("div", { class: retry ? "pfx-pc-frame-error" : "pfx-pc-loading" }, [text]);
    if (retry) {
      const btn = el("button", {}, ["Reload"]);
      btn.addEventListener("click", retry);
      ov.appendChild(btn);
      errorOverlay = ov;
    } else {
      loadingOverlay = ov;
    }
    frameWrap.appendChild(ov);
  }
  function clearOverlays(): void {
    loadingOverlay?.remove(); loadingOverlay = null;
    errorOverlay?.remove(); errorOverlay = null;
  }

  function loadPad(id: string): void {
    requestedPadId = id;
    const pad = pads.find((p) => p.id === id);
    if (!pad) return;
    currentPad = pad;
    currentSchema = (pad.params ?? []).slice();
    currentSelection = null;
    renderVariantSelect();
    if (mobileTopName) mobileTopName.textContent = pad.name;
    opts.onPadChange?.(pad);

    // Tear down previous iframe.
    stopAudioPump();
    clearReadyTimeout();
    if (iframe) { iframe.remove(); iframe = null; }
    clearOverlays();

    const url = resolvePadUrl(pad.file, opts.padBase);
    const fr = document.createElement("iframe");
    fr.setAttribute("allow", "autoplay; fullscreen; xr-spatial-tracking");
    fr.setAttribute("allowfullscreen", "");
    fr.setAttribute("loading", "eager");
    fr.style.cssText = "border:0;width:100%;height:100%";
    fr.src = url;
    iframe = fr;
    frameWrap.appendChild(fr);

    showOverlay("Loading…");

    // Render drawer with manifest schema as seed; pfx:padReady may overwrite.
    renderDrawer();
    renderTiles();

    // After 200ms, ask for the schema in case we missed pfx:padReady.
    setTimeout(() => {
      postToIframe({ type: "pfx:command", command: "requestParams" });
    }, 200);

    // After 250ms, ask the pad to declare its variant/selection list.
    setTimeout(() => {
      postToIframe({ type: "pfx:command", command: "requestSelection" });
    }, 250);

    // After 8s with no padReady, offer a reload.
    readyTimeout = window.setTimeout(() => {
      showOverlay("Pad failed to load.", () => loadPad(id));
    }, 8000);

    fr.addEventListener("load", () => {
      // Iframe loaded; re-request params to be safe.
      postToIframe({ type: "pfx:command", command: "requestParams" });
      // Re-apply persisted values once the pad is alive.
      const stored = persisted[pad.id];
      if (stored) {
        for (const [pid, val] of Object.entries(stored)) {
          postToIframe({ type: "pfx:command", command: "param", value: { id: pid, value: val } });
        }
      }
      startAudioPump();
    });
  }

  function handleMessage(ev: MessageEvent): void {
    if (!iframe || ev.source !== iframe.contentWindow) return;
    const d = ev.data as {
      type?: string;
      name?: string;
      error?: string;
      params?: PadParamSchema[];
      id?: string;
      value?: PadParamValue;
      items?: PadSelectionItem[];
      currentId?: string;
      libId?: string;
      shareId?: string;
    } | null;
    if (!d || typeof d !== "object") return;
    if (d.type === "pfx:padReady" && Array.isArray(d.params)) {
      clearReadyTimeout();
      clearOverlays();
      // If the pad declared a non-empty schema, prefer that over the manifest's
      // (the pad is the source of truth at runtime).
      if (d.params.length > 0) {
        currentSchema = d.params.slice();
        renderDrawer();
      } else if (currentSchema.length === 0) {
        renderDrawer(); // ensures "no controls" hint
      }
      // Re-apply persisted values.
      if (currentPad) {
        const stored = persisted[currentPad.id] ?? {};
        for (const [pid, val] of Object.entries(stored)) {
          postToIframe({ type: "pfx:command", command: "param", value: { id: pid, value: val } });
        }
      }
    } else if (d.type === "pfx:padParamChanged" && typeof d.id === "string") {
      // Reflect into UI without re-emitting downward.
      if (currentPad) {
        if (!persisted[currentPad.id]) persisted[currentPad.id] = {};
        if (d.value !== undefined) persisted[currentPad.id][d.id] = d.value;
      }
      if (d.value !== undefined) controlRefs[d.id]?.(d.value);
      if (currentPad && d.value !== undefined) {
        opts.onParamChange?.(currentPad.id, d.id, d.value);
      }
    } else if (d.type === "pfx:padSelection" && Array.isArray(d.items)) {
      currentSelection = {
        items: d.items.slice(),
        currentId: typeof d.currentId === "string" ? d.currentId : null,
      };
      renderVariantSelect();
      // Restore persisted variant for this pad if we have a match.
      if (currentPad) {
        const saved = persistedSelection[currentPad.id];
        if (saved && currentSelection.items.some((it) => it.id === saved)
            && saved !== currentSelection.currentId) {
          currentSelection.currentId = saved;
          variantSelect.value = saved;
          postToIframe({ type: "pfx:command", command: "selection", value: { id: saved } });
        } else if (currentSelection.currentId) {
          persistSelection(currentPad.id, currentSelection.currentId);
          opts.onSelectionChange?.(currentPad.id, currentSelection.currentId);
        }
      }
    } else if (d.type === "pfx:padSelectionChanged" && typeof d.currentId === "string") {
      if (currentSelection) {
        currentSelection.currentId = d.currentId;
        variantSelect.value = d.currentId;
        if (currentPad) {
          persistSelection(currentPad.id, d.currentId);
          opts.onSelectionChange?.(currentPad.id, d.currentId);
        }
      }
    } else if (d.type === "pfx:padShareId" && typeof d.libId === "string" && typeof d.shareId === "string") {
      libShareIds[d.libId] = d.shareId;
    } else if (d.type === "pfx:shaderImportOk") {
      setSpecialStatus(`Loaded shader: ${d.name ?? "shader"}`, "ok");
    } else if (d.type === "pfx:shaderImportErr" || d.type === "pfx:shaderError") {
      setSpecialStatus(d.error ?? "Shader import failed.", "error");
    } else if (d.type === "pfx:codepenApplied") {
      setSpecialStatus(`Loaded CodePen visualizer: ${d.name ?? "custom"}`, "ok");
    } else if (d.type === "pfx:codepenImportErr" || d.type === "pfx:codepenError") {
      setSpecialStatus(d.error ?? "CodePen import failed.", "error");
    }
  }

  messageHandler = handleMessage;
  window.addEventListener("message", messageHandler);

  // ---------- Manifest load ----------

  async function loadManifest(): Promise<void> {
    let lastErr: unknown = null;
    for (const url of manifestUrls) {
      try {
        const res = await fetch(url, { cache: "no-cache" });
        if (!res.ok) { lastErr = new Error(`HTTP ${res.status}`); continue; }
        const json = await res.json() as { pads?: PadManifestEntry[] };
        if (!json || !Array.isArray(json.pads)) { lastErr = new Error("invalid manifest"); continue; }
        if (opts.includePadIds?.length) {
          const byId = new Map(json.pads.map((pad) => [pad.id, pad]));
          pads = opts.includePadIds
            .map((id) => byId.get(id))
            .filter((pad): pad is PadManifestEntry => Boolean(pad));
        } else {
          pads = json.pads;
        }
        renderTags();
        renderTiles();
        const wanted = requestedPadId && pads.find((p) => p.id === requestedPadId);
        const target = wanted ?? pads[0];
        if (target) loadPad(target.id);
        return;
      } catch (e) { lastErr = e; }
    }
    // All failed.
    root.innerHTML = "";
    const errBox = el("div", { class: "pfx-pc-error" }, [
      "Failed to load pad manifest.",
      el("br"),
      String(lastErr ?? "unknown error"),
      el("br"),
    ]);
    const retry = el("button", {}, ["Retry"]);
    retry.addEventListener("click", () => {
      root.innerHTML = "";
      // Re-mount everything.
      destroyed = false;
      const newOpts = { ...opts };
      createPadContainer(newOpts);
    });
    errBox.appendChild(retry);
    root.appendChild(errBox);
  }

  void loadManifest();

  // ---------- Public handle ----------

  return {
    setPad(id: string) { loadPad(id); },
    setParam(paramId: string, value: PadParamValue) {
      controlRefs[paramId]?.(value);
      sendParam(paramId, value, false);
    },
    getState() {
      return {
        pad: currentPad,
        values: currentPad ? { ...(persisted[currentPad.id] ?? {}) } : {},
      };
    },
    destroy() {
      destroyed = true;
      stopAudioPump();
      clearReadyTimeout();
      if (messageHandler) window.removeEventListener("message", messageHandler);
      messageHandler = null;
      if (iframe) { iframe.remove(); iframe = null; }
      root.innerHTML = "";
      root.classList.remove("pfx-pc", "pfx-pc-desktop", "pfx-pc-mobile");
    },
  };
}
